import { Action, Thunk } from 'easy-peasy';

import {
  IAddCrisisAllocationPayload,
  ICrisisAllocation,
  ICrisisCall,
  ICrisisCallsAllocatedClients,
  ICrisisCallTeamMember,
  IEditCrisisAddressPayload,
  IEditManualCrisisAllocationAddressPayload,
  IManualCrisisAllocationPayload,
  IResolveCrisisCallPayload,
} from '~/services/api/crisis/types';

export const ALLOCATIONS_FILTER = {
  CRISIS_ONLY: 'crisis',
  ALL: 'all',
} as const;

export type TAllocationsFilter = (typeof ALLOCATIONS_FILTER)[keyof typeof ALLOCATIONS_FILTER];
export type IThunkAddCrisisAllocation = Thunk<
  ICrisisAllocation,
  { clinicId: string; payload: IAddCrisisAllocationPayload }
>;
export type IThunkAddCrisisAllocationManual = Thunk<
  ICrisisAllocation,
  { clinicId: string; payload: IManualCrisisAllocationPayload }
>;
export type IThunkDeleteCrisisAllocation = Thunk<
  ICrisisAllocation,
  { clinicId: string; id: number }
>;
export type IThunkEditCrisisAllocation = Thunk<
  ICrisisAllocation,
  { clinicId: string; id: string; memo: string }
>;
export type IActionSetCrisisAllocations = Action<ICrisisStore, ICrisisAllocation>;
export type IActionSetAllocationsFilter = Action<ICrisisStore, TAllocationsFilter>;

export type IThunkEditCrisisAddress = Thunk<
  ICrisisCall,
  { clinicId: number; id: number; data: IEditCrisisAddressPayload }
>;
export type IThunkEditManualCrisisAllocationAddress = Thunk<
  ICrisisCallsAllocatedClients,
  {
    clinicId: string;
    followUpAllocationId: number;
    data: IEditManualCrisisAllocationAddressPayload;
  }
>;

export type IActionSetCrisisCalls = Action<ICrisisStore, ICrisisCall[]>;
export type IActionSetCrisisCallTeamMembers = Action<ICrisisStore, ICrisisCallTeamMember[]>;
export type IActionAddOrUpdateCrisisCall = Action<ICrisisStore, ICrisisCall>;
export type IActionRemoveCrisisCall = Action<ICrisisStore, { crisisCallId: number }>;
export type IActionAddCrisisCallTeamMemberAllocations = Action<
  ICrisisStore,
  ICrisisCallTeamMember[]
>;
export type IActionRemoveCrisisCallTeamMemberAllocations = Action<
  ICrisisStore,
  { allocationClientIds: number[] }
>;
export type IActionRemoveCrisisCallTeamMemberAllocationsByCrisisCall = Action<
  ICrisisStore,
  { crisisCallId: number }
>;
export type IActionUpdateCrisisCallTeamMemberAllocations = Action<
  ICrisisStore,
  { createdAllocations: ICrisisCallTeamMember[]; deletedClientAllocationsId: number[] }
>;
export type IActionStartCrisisVisit = Action<
  ICrisisStore,
  { userId?: number; clientId?: number; crisisCallId?: number; startTimeUtc?: string }
>;
export type IActionFinishCrisisVisit = Action<
  ICrisisStore,
  {
    userId?: number;
    clientId?: number;
    crisisCallId?: number;
    isCanceled: boolean;
    otherVisitsExist?: boolean;
  }
>;
export type IThunkGetCrisisCalls = Thunk<ICrisisStore, { clinicId: string }>;
export type IThunkGetCrisisCallTeamMembers = Thunk<
  ICrisisStore,
  { clinicId: string; onlyCrisis: boolean }
>;
export type IThunkCrisisCallResolve = Thunk<ICrisisStore, IResolveCrisisCallPayload>;
export interface ICrisisActions {
  setCrisisAllocation: IActionSetCrisisAllocations;
  setCrisisCalls: IActionSetCrisisCalls;
  setCrisisCallTeamMembers: IActionSetCrisisCallTeamMembers;
  setAllocationsFilter: IActionSetAllocationsFilter;
  addOrUpdateCrisisCall: IActionAddOrUpdateCrisisCall;
  removeCrisisCall: IActionRemoveCrisisCall;
  addCrisisCallTeamMemberAllocations: IActionAddCrisisCallTeamMemberAllocations;
  removeCrisisCallTeamMemberAllocations: IActionRemoveCrisisCallTeamMemberAllocations;
  removeCrisisCallTeamMemberAllocationsByCrisisCall: IActionRemoveCrisisCallTeamMemberAllocationsByCrisisCall;
  updateCrisisCallTeamMemberAllocations: IActionUpdateCrisisCallTeamMemberAllocations;
  startCrisisVisit: IActionStartCrisisVisit;
  finishCrisisVisit: IActionFinishCrisisVisit;
}
interface ICrisisThunks {
  onAddCrisisAllocation: IThunkAddCrisisAllocation;
  onAddCrisisAllocationManual: IThunkAddCrisisAllocationManual;
  onDeleteCrisisAllocation: IThunkDeleteCrisisAllocation;
  onEditCrisisAllocation: IThunkEditCrisisAllocation;
  onEditCrisisAddress: IThunkEditCrisisAddress;
  onEditManualCrisisAllocationAddress: IThunkEditManualCrisisAllocationAddress;
  onGetCrisisCalls: IThunkGetCrisisCalls;
  onGetCrisisCallTeamMembers: IThunkGetCrisisCallTeamMembers;
  onCrisisCallResolve: IThunkCrisisCallResolve;
}
export interface ICrisisStore {
  current: ICrisisAllocation;
  crisisCalls: ICrisisCall[];
  crisisCallTeamMembers: ICrisisCallTeamMember[];
  allocationsFilter: TAllocationsFilter;
}

export type ICrisisModel = ICrisisStore & ICrisisActions & ICrisisThunks;
